/**
 * API методы для SuperAdmin: управление тенантами, статистика, аудит
 * Дата: 29.01.2026 - TODO-043: Итерация 4
 * Связан с: internal/handler/admin.go, internal/handler/tenant.go
 */
import { apiClient } from './client';

export interface Tenant {
  id: string;
  slug: string;
  name: string;
  is_active: boolean;
  config?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface CreateTenantRequest {
  slug: string;
  name: string;
  config?: Record<string, any>;
}

export interface UpdateTenantRequest {
  name?: string;
  config?: Record<string, any>;
}

export interface SystemStats {
  total_tenants: number;
  active_tenants: number;
  total_users: number;
  total_messages: number;
  messages_today?: number;
}

export interface AuditLogEntry {
  id: string;
  tenant_id?: string;
  user_id: string;
  username: string;
  action: string;
  resource_type: string;
  resource_id?: string;
  ip_address: string;
  user_agent: string;
  old_values?: Record<string, any>;
  new_values?: Record<string, any>;
  created_at: string;
}

export interface StaffIdentity {
  id: string;
  tenant_id: string;
  tenant_name?: string;
  label: string;
  comment?: string;
  phone_normalized?: string;
  telegram_id?: string;
  max_id?: string;
  is_active: boolean;
  source: string;
  created_at: string;
  updated_at: string;
}

export interface StaffIdentityRequest {
  tenant_id: string;
  label: string;
  comment?: string;
  phone?: string;
  telegram_id?: string;
  max_id?: string;
  is_active?: boolean;
}

export const adminApi = {
  // System statistics
  getStats: async (): Promise<SystemStats> => {
    const response = await apiClient.get('/admin/stats');
    return response.data;
  },

  // Tenants management
  listTenants: async (): Promise<{ tenants: Tenant[] }> => {
    const response = await apiClient.get('/admin/tenants');
    return response.data;
  },

  getTenant: async (id: string): Promise<Tenant> => {
    const response = await apiClient.get(`/admin/tenants/${id}`);
    return response.data;
  },

  createTenant: async (data: CreateTenantRequest): Promise<Tenant> => {
    const response = await apiClient.post('/admin/tenants', data);
    return response.data;
  },

  updateTenant: async (id: string, data: UpdateTenantRequest): Promise<Tenant> => {
    const response = await apiClient.patch(`/admin/tenants/${id}`, data);
    return response.data;
  },

  updateTenantConfig: async (id: string, config: Record<string, any>): Promise<Tenant> => {
    const response = await apiClient.patch(`/admin/tenants/${id}/config`, { config });
    return response.data;
  },

  deactivateTenant: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/tenants/${id}`);
  },

  // Audit log
  getAuditLog: async (params?: { tenant_id?: string; limit?: number; offset?: number }): Promise<{ entries: AuditLogEntry[]; total: number }> => {
    const response = await apiClient.get('/admin/audit', { params });
    return response.data;
  },

  // Дата: 30.01.2026 — Пользователи ВСЕХ тенантов (SuperAdmin)
  listAllUsers: async (params?: { limit?: number; offset?: number }): Promise<{ users: AllUsersItem[]; total: number }> => {
    const response = await apiClient.get('/admin/users', { params });
    return response.data;
  },

  listStaffIdentities: async (params?: { tenant_id?: string; search?: string; is_active?: boolean; limit?: number; offset?: number }): Promise<{ items: StaffIdentity[]; total: number }> => {
    const response = await apiClient.get('/admin/staff-identities', { params });
    return response.data;
  },

  createStaffIdentity: async (data: StaffIdentityRequest): Promise<StaffIdentity> => {
    const response = await apiClient.post('/admin/staff-identities', data);
    return response.data;
  },

  updateStaffIdentity: async (id: string, data: Partial<StaffIdentityRequest>): Promise<StaffIdentity> => {
    const response = await apiClient.patch(`/admin/staff-identities/${id}`, data);
    return response.data;
  },
};

// Дата: 30.01.2026 — Тип для пользователей всех тенантов
export interface AllUsersItem {
  id: string;
  tenant_id?: string;
  username: string;
  email: string;
  full_name: string;
  role_name: string;
  is_active: boolean;
  created_at: string;
}
